import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';

// Cuando se envia el recordatorio: devuelve la fecha, o null para "ahora"
const mananaA = (hora) => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hora, 0, 0, 0);
  return d;
};
const enHoras = (h) => new Date(Date.now() + h * 60 * 60 * 1000);

const OPCIONES = [
  { id: 'ahora', label: 'Ahora', fecha: () => null },
  { id: '1h', label: 'En 1 hora', fecha: () => enHoras(1) },
  { id: '3h', label: 'En 3 horas', fecha: () => enHoras(3) },
  { id: 'manana', label: 'Mañana a las 9:00', fecha: () => mananaA(9) },
];

const aInputLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatear = (iso) =>
  new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function TicketRecordatorio({ ticketId, asignadoId }) {
  const { user } = useAuth();
  const { alertSuccess, alertError } = useNotificationContext();
  const [abierto, setAbierto] = useState(false);
  const [equipo, setEquipo] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [destinatario, setDestinatario] = useState('');
  const [opcion, setOpcion] = useState('ahora');
  const [personalizada, setPersonalizada] = useState('');
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const contenedor = useRef(null);

  const cargarPendientes = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}/recordatorios`);
      setPendientes(Array.isArray(data) ? data : []);
    } catch {
      setPendientes([]);
    }
  }, [ticketId]);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  // Al abrir: trae al equipo y deja como destinatario al tecnico asignado (o a uno mismo)
  useEffect(() => {
    if (!abierto) return;
    api.get('/tickets/soporte/usuarios')
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : [];
        setEquipo(lista);
        setDestinatario((actual) => {
          if (actual) return actual;
          const pref = lista.find((u) => u.id === asignadoId) || lista.find((u) => u.id === user?.id) || lista[0];
          return pref ? String(pref.id) : '';
        });
      })
      .catch(() => setEquipo([]));
  }, [abierto, asignadoId, user?.id]);

  useEffect(() => {
    if (!abierto) return undefined;
    const cerrarFuera = (e) => {
      if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', cerrarFuera);
    return () => document.removeEventListener('mousedown', cerrarFuera);
  }, [abierto]);

  const nombreDe = (u) => `${u.nombre || ''} ${u.apellido || ''}`.trim() || u.username || `Usuario ${u.id}`;

  const enviar = async () => {
    if (!destinatario) {
      alertError('Elige a quién recordarle.');
      return;
    }
    let fecha = null;
    if (opcion === 'otra') {
      if (!personalizada) {
        alertError('Elige la fecha y hora del recordatorio.');
        return;
      }
      fecha = new Date(personalizada);
    } else {
      fecha = OPCIONES.find((o) => o.id === opcion).fecha();
    }
    if (fecha && fecha.getTime() <= Date.now()) {
      alertError('El recordatorio debe ser a una hora futura.');
      return;
    }
    const persona = equipo.find((u) => String(u.id) === String(destinatario));
    setGuardando(true);
    try {
      await api.post(`/tickets/${ticketId}/recordatorio`, {
        destinatarioId: Number(destinatario),
        fecha: fecha ? fecha.toISOString() : undefined,
        nota: nota.trim() || undefined,
      });
      alertSuccess(fecha
        ? `Recordatorio programado para ${persona ? nombreDe(persona) : 'la persona'} el ${formatear(fecha.toISOString())}.`
        : `Recordatorio enviado a ${persona ? nombreDe(persona) : 'la persona'}.`);
      setNota('');
      setAbierto(false);
      cargarPendientes();
    } catch (e) {
      alertError(e.response?.data?.message || 'No se pudo enviar el recordatorio.');
    } finally {
      setGuardando(false);
    }
  };

  const cancelar = async (id) => {
    try {
      await api.delete(`/tickets/recordatorio/${id}`);
      alertSuccess('Recordatorio cancelado.');
      cargarPendientes();
    } catch {
      alertError('No se pudo cancelar el recordatorio.');
    }
  };

  return (
    <div className="relative" ref={contenedor}>
      <button
        onClick={() => setAbierto((v) => !v)}
        title="Recordarle a alguien del equipo que revise este ticket"
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${pendientes.length
          ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'}`}
      >
        <Bell className="w-4 h-4" />
        Recordar
        {pendientes.length > 0 && (
          <span className="ml-0.5 px-1.5 rounded-full bg-amber-200 text-amber-800 text-xs font-semibold">{pendientes.length}</span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-80 z-30 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-4 text-sm">
          <p className="font-semibold text-gray-900 dark:text-white">Recordar este ticket</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
            La persona recibirá un correo y una notificación para que lo revise.
          </p>

          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Recordar a</label>
          <select
            value={destinatario}
            onChange={(e) => setDestinatario(e.target.value)}
            className="w-full p-1.5 border rounded text-sm bg-white dark:bg-gray-700 dark:border-gray-600"
          >
            {equipo.map((u) => (
              <option key={u.id} value={u.id}>
                {nombreDe(u)}{u.id === user?.id ? ' (yo)' : ''}{u.id === asignadoId ? ' · asignado' : ''}
              </option>
            ))}
          </select>

          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mt-3 mb-1">Cuándo</label>
          <div className="space-y-1.5">
            {OPCIONES.map((o) => (
              <label key={o.id} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-200">
                <input type="radio" name="recordatorio-opcion" checked={opcion === o.id} onChange={() => setOpcion(o.id)} />
                {o.label}
              </label>
            ))}
            <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-200">
              <input type="radio" name="recordatorio-opcion" checked={opcion === 'otra'} onChange={() => setOpcion('otra')} />
              Elegir fecha y hora
            </label>
            {opcion === 'otra' && (
              <input
                type="datetime-local"
                value={personalizada}
                min={aInputLocal(new Date())}
                onChange={(e) => setPersonalizada(e.target.value)}
                className="w-full mt-1 p-1.5 border rounded text-sm bg-white dark:bg-gray-700 dark:border-gray-600"
              />
            )}
          </div>

          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Mensaje opcional (ej. el cliente está esperando respuesta)"
            className="w-full mt-3 p-2 border rounded text-sm bg-white dark:bg-gray-700 dark:border-gray-600"
          />

          <button
            onClick={enviar}
            disabled={guardando}
            className="w-full mt-3 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {opcion === 'ahora' ? 'Enviar recordatorio' : 'Programar recordatorio'}
          </button>

          {pendientes.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Programados</p>
              <ul className="space-y-1">
                {pendientes.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 text-xs text-gray-700 dark:text-gray-200">
                    <span className="truncate">{p.destinatario} · {formatear(p.fecha)}</span>
                    <button onClick={() => cancelar(p.id)} className="text-red-600 dark:text-red-400 hover:underline shrink-0">Cancelar</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
